import type * as THREE from 'three/webgpu';

/**
 * 测量浮窗展示行。
 */
export interface MeasurePropertyRow {
    /** 属性标签。 */
    label: string;
    /** 属性值文本。 */
    value: string;
}

/**
 * 测量 callout 浮窗视图状态（供 web-client 只读渲染）。
 */
export interface MeasureCalloutViewState {
    /** 场景对象 UUID。 */
    objectId: string;
    /** 对象显示名称。 */
    entityName: string;
    /** 拓扑/基准类型中文标签。 */
    entityKindLabel: string;
    /** 世界坐标拾取点。 */
    worldPickPoint: THREE.Vector3;
    /** 属性行列表。 */
    rows: MeasurePropertyRow[];
}

/**
 * 测量线三维渲染模式。
 *
 * @deprecated 请改用 {@link MeasureLineVisibilityGroup}；保留枚举仅兼容旧测试引用。
 */
export enum MeasureDxDyDzLineRenderMode {
    /** 仅绘制两点间直线距离线（黑）。 */
    DistanceOnly = 'distanceOnly',
    /** 绘制 ΔX / ΔY / ΔZ 分量线及直线距离线（当前完整阶梯折线）。 */
    Full = 'full',
    /** 仅绘制 ΔX / ΔY / ΔZ 三条分量线（不含直线距离）。 */
    XyzOnly = 'xyzOnly',
    /** 仅绘制 ΔX 分量线。 */
    DxOnly = 'dxOnly',
    /** 仅绘制 ΔY 分量线。 */
    DyOnly = 'dyOnly',
    /** 仅绘制 ΔZ 分量线。 */
    DzOnly = 'dzOnly',
    /** 不绘制任何 ΔX/ΔY/ΔZ 与距离线。 */
    Hidden = 'hidden',
}

/**
 * 测量线可视化选中项（互斥单选：点选/hover 某一结果项后才显示对应虚线）。
 */
export enum MeasureLineVisibilityGroup {
    /** 不显示任何测量线。 */
    None = 'none',
    /** 仅显示直线距离线。 */
    Distance = 'distance',
    /** 仅显示垂直距离线（及正交辅助线）。 */
    NormalDistance = 'normalDistance',
    /** 仅显示 ΔX 分量线。 */
    Dx = 'dx',
    /** 仅显示 ΔY 分量线。 */
    Dy = 'dy',
    /** 仅显示 ΔZ 分量线。 */
    Dz = 'dz',
}

/**
 * ΔX / ΔY / ΔZ 测量线三维渲染输入。
 */
export interface MeasureDxDyDzLineRenderInput {
    /** 第一点世界坐标（基准点）。 */
    from: THREE.Vector3;
    /** 第二点世界坐标（目标点）。 */
    to: THREE.Vector3;
    /** X 方向分量差。 */
    dx: number;
    /** Y 方向分量差。 */
    dy: number;
    /** Z 方向分量差。 */
    dz: number;
    /** 两点直线距离。 */
    distance: number;
}

/**
 * 双选测量浮窗种类。
 */
export enum MeasurePairMeasurementCalloutKind {
    /** 直线距离。 */
    Distance = 'distance',
    /** X 分量。 */
    Dx = 'dx',
    /** Y 分量。 */
    Dy = 'dy',
    /** Z 分量。 */
    Dz = 'dz',
    /** 垂直距离。 */
    NormalDistance = 'normalDistance',
}

/**
 * 双选测量结果区条目（每种测量量对应一项，点选后驱动三维虚线）。
 */
export interface MeasurePairMeasurementCalloutItem {
    /** 结果项唯一 ID（供 UI 绑定选中态）。 */
    calloutId: string;
    /** 结果项种类。 */
    kind: MeasurePairMeasurementCalloutKind;
    /** 展示标签。 */
    label: string;
    /** 展示值。 */
    value: string;
    /** 对应测量线中点（世界坐标，米；供调试或后续扩展）。 */
    worldAnchor: THREE.Vector3;
}

/**
 * 两实体距离测量视图状态（选中两个对象时）。
 */
export interface MeasurePairMeasurementViewState {
    /** 第一个选中实体上的最近点。 */
    from: THREE.Vector3;
    /** 第二个选中实体上的最近点。 */
    to: THREE.Vector3;
    /** 直线距离。 */
    distance: number;
    /** X 分量差。 */
    dx: number;
    /** Y 分量差。 */
    dy: number;
    /** Z 分量差。 */
    dz: number;
    /** 垂直距离；仅在与直线距离不等时有值。 */
    normalDistance?: number;
    /** 垂直距离线起点（点侧）；无垂直距离时为空。 */
    normalFrom?: THREE.Vector3;
    /** 垂直距离线终点（无限几何垂足）；无垂直距离时为空。 */
    normalTo?: THREE.Vector3;
    /** 正交辅助线起点（有限体最近点）；无辅助线时为空。 */
    transverseFrom?: THREE.Vector3;
    /** 正交辅助线终点（垂足后延伸段）；无辅助线时为空。 */
    transverseTo?: THREE.Vector3;
    /** 多路测量结果项（供测量浮窗下方展示）。 */
    callouts: MeasurePairMeasurementCalloutItem[];
}

/**
 * 垂直距离测量线三维渲染输入。
 */
export interface MeasureNormalDistanceLineRenderInput {
    /** 垂直距离线起点（点侧）。 */
    from: THREE.Vector3;
    /** 垂直距离线终点（无限几何垂足）。 */
    to: THREE.Vector3;
    /** 正交辅助线起点（有限体最近点）；无辅助线时可省略。 */
    transverseFrom?: THREE.Vector3;
    /** 正交辅助线终点（垂足后延伸段）；无辅助线时可省略。 */
    transverseTo?: THREE.Vector3;
}

/**
 * 双选距离显示策略（结果区是否列出 ΔX / ΔY / ΔZ）。
 *
 * 设计说明（对齐 Onshape）：
 * 1. 本策略只控制结果区是否列出分量行，不再直接驱动三维测量线；
 * 2. 三维测量线由 {@link MeasureLineVisibilityGroup} 点选控制。
 */
export interface MeasureDisplayPolicy {
    /** 是否在结果区列出 ΔX / ΔY / ΔZ 分量行。 */
    showDxDyDzMeasurement: boolean;
}

/**
 * 创建进入测量时的默认显示策略。
 *
 * 对齐 Onshape：默认列出 XYZ 分量行；测量线仍默认隐藏直至点选。
 *
 * @returns 默认显示策略。
 */
export function createDefaultMeasureDisplayPolicy(): MeasureDisplayPolicy {
    return {
        showDxDyDzMeasurement: true,
    };
}

/**
 * 测量 callout 状态变化事件。
 */
export interface MeasureCalloutChangeEvent {
    /** 当前浮窗视图状态；无活动锚点时为空。 */
    viewState: MeasureCalloutViewState | null;
    /** 双选距离测量状态；非双选或未求解时为空。 */
    pairMeasurement: MeasurePairMeasurementViewState | null;
    /** 当前测量线可视化选中组。 */
    lineVisibilityGroup: MeasureLineVisibilityGroup;
}
